import React, { useCallback, useEffect } from 'react';
import { GuestProfile, Reservation, reservationService, propertyQuery } from '@lib/state';
import {
  useObservable,
  Section,
  LoadingIndicator,
  FormHub,
  GuestActivity,
  useGuestCurrentReservations,
  useGuestFutureReservations,
  CurrentGuestStays,
  FutureGuestStays,
  GuestRelationsForm,
} from '@lib/common';
import {
  guestManagementService,
  guestManagementQuery,
  corporateAccountService,
  corporateAccountQuery,
} from 'app/state';
import { BreadcrumbBackButton, AuditLogs } from 'app/shared';
import { GuestProfileForm } from '../components';
import { GuestDetailsRouteProps } from '../guest-detail-route-props';
import { useHistory } from 'react-router-dom';

export const GuestDetailsPage: React.FC<GuestDetailsRouteProps> = ({ match }) => {
  const property = useObservable(propertyQuery.activeProperty, 'async');
  const userId = match.params.userId;
  const history = useHistory();

  const guest = useObservable(guestManagementQuery.activeGuest, 'async');
  const corporateAccounts = useObservable(corporateAccountQuery.corporations, 'async');

  useEffect(() => {
    guestManagementService.getGuestById(userId);
    guestManagementService.select(userId);

    // Remove active guest
    return () => guestManagementService.select();
  }, [userId]);

  useEffect(() => {
    if (property?.customerId) {
      corporateAccountService.getAllCorporateAccounts(property.customerId);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.customerId]);
  const current = useGuestCurrentReservations(userId, property?.id);
  const future = useGuestFutureReservations(userId, property?.id);

  const propertyId = property?.id;

  const onExport = useCallback(
    status => {
      if (propertyId)
        reservationService.exportGuestReservations({
          userId,
          propertyId,
          status,
        });
    },
    [userId, propertyId]
  );

  const onConfirmationClick = (reservation: Reservation) => {
    history.push(`/reservations/${reservation.id}?guestId=${guest?.userId}`);
  };

  if (!property?.id) return null;
  if (!guest) return <LoadingIndicator loadingText="Loading Guest" fillContainer />;

  return (
    <Section>
      <BreadcrumbBackButton destination="/guests" />
      <FormHub
        onSubmit={(guest: GuestProfile) => {
          guestManagementService.updateGuestProfile({ ...guest, userId });
        }}
      >
        <GuestProfileForm guest={guest} />
      </FormHub>
      <GuestRelationsForm guest={guest} readOnly={true} />
      <CurrentGuestStays
        userId={userId}
        reservations={current.reservations}
        page={current.page}
        onLoadMore={current.loadMore}
        onExport={onExport}
        onConfirmationClick={onConfirmationClick}
        createForGuest={true}
        corporateAccounts={corporateAccounts}
      />
      <FutureGuestStays
        userId={userId}
        reservations={future.reservations}
        page={future.page}
        onLoadMore={future.loadMore}
        onExport={onExport}
        onConfirmationClick={onConfirmationClick}
        createForGuest={true}
        corporateAccounts={corporateAccounts}
      />
      <GuestActivity userId={userId} createForGuest={true} corporateAccounts={corporateAccounts} />
      <Section title="Audit Logs" disableGutters>
        <AuditLogs scope="user" id={userId} />
      </Section>
    </Section>
  );
};
